import api from '../api';
import { cache } from '../../utils/cache';
import type {
  CreateFinishedGoodsProductionDraftNewRequest,
  CreateFinishedGoodsProductionDraftRequest,
  CreateFinishedGoodsProductionDraftResponse,
  FinishedGoodIngredient,
  FinishedGoodsCostEstimateRequest,
  FinishedGoodsCostEstimateResponse,
  FinishedGoodsProductionDetail,
  FinishedGoodsProductionListItem,
  FinishedGoodsProductionRequest,
  FinishedGoodsProductionResponse,
  FinishedGoodsProductionStatus,
  CompleteFinishedGoodsProductionDraftRequest,
} from './inventoryMappers';

export const finishedGoodsService = {
  produceFinishedGoods: async (
    payload: FinishedGoodsProductionRequest
  ): Promise<FinishedGoodsProductionResponse> => {
    const response = await api.post('/inventory/finished-goods/produce', payload);
    cache.delete('inventory_all');
    cache.invalidate('reports_');
    return response.data;
  },
  listFinishedGoodsProductions: async (params?: {
    status?: FinishedGoodsProductionStatus | 'all';
    finishedGoodId?: string;
  }): Promise<FinishedGoodsProductionListItem[]> => {
    const response = await api.get('/inventory/finished-goods/productions', { params });
    const data = response.data?.productions ?? response.data ?? [];
    return Array.isArray(data) ? (data as FinishedGoodsProductionListItem[]) : [];
  },
  getFinishedGoodsProduction: async (productionId: string): Promise<FinishedGoodsProductionDetail> => {
    const response = await api.get(`/inventory/finished-goods/productions/${productionId}`);
    return response.data as FinishedGoodsProductionDetail;
  },
  // Read-only COGS preview. Priced by the backend with the same FIFO logic as an
  // actual production so the modal's total/unit cost matches what will be booked.
  estimateFinishedGoodsProductionCost: async (
    payload: FinishedGoodsCostEstimateRequest
  ): Promise<FinishedGoodsCostEstimateResponse> => {
    const response = await api.post('/inventory/finished-goods/produce/estimate', payload);
    return response.data as FinishedGoodsCostEstimateResponse;
  },
  createFinishedGoodsProductionDraft: async (
    payload: CreateFinishedGoodsProductionDraftRequest
  ): Promise<CreateFinishedGoodsProductionDraftResponse> => {
    const response = await api.post('/inventory/finished-goods/productions/draft', payload);
    return response.data as CreateFinishedGoodsProductionDraftResponse;
  },
  createFinishedGoodsProductionDraftNew: async (
    payload: CreateFinishedGoodsProductionDraftNewRequest
  ): Promise<CreateFinishedGoodsProductionDraftResponse> => {
    const response = await api.post('/inventory/finished-goods/productions/draft/new', payload);
    return response.data as CreateFinishedGoodsProductionDraftResponse;
  },
  completeFinishedGoodsProductionDraft: async (
    productionId: string,
    payload: CompleteFinishedGoodsProductionDraftRequest
  ): Promise<FinishedGoodsProductionResponse> => {
    const response = await api.post(`/inventory/finished-goods/productions/${productionId}/complete`, payload);
    cache.delete('inventory_all');
    cache.invalidate('reports_');
    return response.data as FinishedGoodsProductionResponse;
  },
  cancelFinishedGoodsProductionDraft: async (productionId: string): Promise<{ status: FinishedGoodsProductionStatus }> => {
    const response = await api.post(`/inventory/finished-goods/productions/${productionId}/cancel`);
    cache.invalidate('reports_');
    return response.data as { status: FinishedGoodsProductionStatus };
  },
  getFinishedGoodIngredients: async (finishedGoodId: string): Promise<FinishedGoodIngredient[]> => {
    try {
      const response = await api.get(`/inventory/finished-goods/${finishedGoodId}/ingredients`);
      const data = response.data?.ingredients ?? response.data ?? [];
      return Array.isArray(data) ? (data as FinishedGoodIngredient[]) : [];
    } catch {
      return [];
    }
  },
  getFinishedGoodMiscCosts: async (finishedGoodId: string): Promise<Array<{ name: string; unit_price: number; unit: number; total_price: number }>> => {
    try {
      const response = await api.get(`/inventory/finished-goods/${finishedGoodId}/misc-costs`);
      const data = response.data?.misc_costs ?? response.data ?? [];
      return Array.isArray(data) ? data as Array<{ name: string; unit_price: number; unit: number; total_price: number }> : [];
    } catch {
      return [];
    }
  },
};
